// "While you were away", and the region travel transition.
//
// The live hunt runs on the server only while this page is connected (and for
// the grace after). So the card reports two honest things:
//  - a hidden tab: every event the client received while hidden, counted here
//    as it arrives (the server sends every event while connected);
//  - a closed page: hello carries `away`, what the server ledger recorded
//    since the last session ended (market sales, halls lost, Guild Wars your
//    guild fought with you on the roster), and the away hunting the server
//    credited at log-in (shared/offline.ts): coins and XP only, never catches.
// The card shows only when there is something to report. It sits just under
// the hunt card on wide screens and under the field on phones (never over the
// hunt card), never blocks, and changes no page unless the player presses
// "See new catches".
import { CONFIG } from '../shared/config.ts';
import { AREA, GROUND, REGIONS } from '../shared/grounds.ts';
import { ITEM } from '../shared/items.ts';
import { SPECIES } from '../shared/species.ts';
import { hallName, isHall } from '../shared/halls.ts';
import { trainerXpToNext } from '../shared/xp.ts';
import { gradeOf, type Grade } from '../shared/grade.ts';
import { awayPaceName } from '../shared/offline.ts';
import type { AwayDTO, GameEvent } from '../shared/protocol.ts';
import { S } from './state.ts';
import { esc, full, num, cname, summary, duration } from './fmt.ts';
import { patch } from './dom.ts';
import { fieldWatching } from './field.ts';

/** Shorter absences get no card. */
export const AWAY_MIN_MS = 5 * 60_000;
const TRAVEL_MS = 2000;

interface Tally {
  from: number;
  level: number; xp: number; coins: number;
  /** Team levels when the tab was hidden. */
  levels: Map<number, number>;
  kills: number; killXp: number; benchXp: number;
  caught: number; released: number; grades: Partial<Record<Grade, number>>; auras: number;
  knockouts: number; xpLost: number; revives: number;
  moves: string[];
}

let tally: Tally | null = null;
let card: { title: string; rows: string[]; since: number; caught: boolean } | null = null;

function startTally() {
  const t = S.trainer;
  if (!t) return;
  tally = {
    from: Date.now(), level: t.level, xp: t.xp, coins: t.coins,
    levels: new Map(t.team.flatMap((id) => { const c = S.creatures.get(id); return c ? [[id, c.level] as [number, number]] : []; })),
    kills: 0, killXp: 0, benchXp: 0, caught: 0, released: 0, grades: {}, auras: 0, knockouts: 0, xpLost: 0, revives: 0, moves: [],
  };
}

const where = (id: string) => { const g = GROUND[id]; return g ? `${esc(AREA[g.area].name)} (Lv ${g.level})` : 'another hunt'; };

/** Called for every batch of game events, hidden or not; counts only while hidden. */
export function awayEvents(evs: GameEvent[]) {
  if (!tally) return;
  for (const e of evs) {
    switch (e.k) {
      case 'down':
        tally.kills++; tally.killXp += e.xp;
        for (const [, xp] of e.bench ?? []) tally.benchXp += xp;
        break;
      case 'catch':
        tally.caught++;
        if (e.sold) tally.released++;
        if (e.creature.rolls.aura) tally.auras++;
        { const g = gradeOf(summary(e.creature).rating); tally.grades[g] = (tally.grades[g] ?? 0) + 1; }
        break;
      case 'rest': if (e.xpLost !== undefined) { tally.knockouts++; tally.xpLost += e.xpLost; } break;
      case 'revive': tally.knockouts++; tally.revives++; break;
      case 'stopped': tally.knockouts++; tally.xpLost += e.xpLost; tally.moves.push('Out of potions and Revives: your team went back to Kinhall'); break;
      case 'retreat': tally.moves.push(`Moved from ${where(e.from)} to ${where(e.to)} after ${CONFIG.wipeRetreatCount} knockouts`); break;
      case 'move': tally.moves.push(`Auto-move: ${where(e.from)} to ${where(e.to)}, the best Safe hunt for ${e.by === 'xp' ? 'XP' : 'coins'}`); break;
    }
  }
}

/** Trainer XP between two (level, xp) points, counting whole levels in between. */
function xpBetween(l0: number, x0: number, l1: number, x1: number) {
  let n = x1 - x0;
  for (let l = l0; l < l1; l++) n += trainerXpToNext(l);
  return n;
}

function finishTally() {
  const t = S.trainer, a = tally;
  tally = null;
  if (!t || !a) return;
  const ms = Date.now() - a.from;
  if (ms < AWAY_MIN_MS) return;
  // Nothing happened (the connection was paused the whole time): no card.
  if (!a.kills && !a.caught && !a.knockouts && !a.moves.length && t.coins === a.coins && t.level === a.level && t.xp === a.xp) return;
  const rows: string[] = [];
  rows.push(`<span>Defeated</span><b>${full(a.kills)}</b>`);
  const net = xpBetween(a.level, a.xp, t.level, t.xp);
  rows.push(`<span>Trainer XP</span><b>+${num(a.killXp)}${t.level > a.level ? ` <span class="muted">(Lv ${a.level} → ${t.level})</span>` : ''}</b>`);
  for (const [id, l0] of a.levels) {
    const c = S.creatures.get(id);
    if (c && c.level > l0) rows.push(`<span>${esc(cname(c))}</span><b>Lv ${l0} → ${c.level}</b>`);
  }
  if (a.benchXp) rows.push(`<span>Shrine XP</span><b>+${num(a.benchXp)}</b>`);
  const coins = t.coins - a.coins;
  rows.push(`<span>Coins, net</span><b>${coins >= 0 ? '+' : '−'}${num(Math.abs(coins))}</b>`);
  if (a.caught) {
    const parts = (['S+', 'S', 'A', 'B', 'C'] as Grade[]).filter((g) => a.grades[g]).map((g) => `${g} ${a.grades[g]}`).join(' · ');
    rows.push(`<span>Caught</span><b>${full(a.caught)} <span class="muted">(${parts}${a.released ? ` · ${a.released} released` : ''})</span></b>`);
  }
  if (a.auras) rows.push(`<span>Auras</span><b>${a.auras}</b>`);
  if (a.knockouts) rows.push(`<span>Knockouts</span><b>${a.knockouts} <span class="muted">(${[a.xpLost ? `−${num(a.xpLost)} XP` : '', a.revives ? `${a.revives} Revive${a.revives === 1 ? '' : 's'} used` : ''].filter(Boolean).join(' · ') || 'no XP lost'})</span></b>`);
  if (a.knockouts && net !== a.killXp) rows.push(`<span>Trainer XP, net</span><b>${net >= 0 ? '+' : '−'}${num(Math.abs(net))}</b>`);
  for (const m of a.moves.slice(-3)) rows.push(`<span class="wide">${m}</span>`);
  card = { title: `While you were away (${awayLength(ms)})`, rows, since: a.from, caught: a.caught - a.released > 0 };
}

const ordinal = (n: number) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;

function awayLength(ms: number) { return duration(ms).replace(/^~|^about /, ''); }

/** hello after the player was unloaded: the ledger since the last session. */
export function awayFromServer(away: AwayDTO | undefined, now: number) {
  // Sales, lost halls, war results and away hunting are news.
  const wars = away?.wars ?? [], halls = away?.halls ?? [], hunt = away?.hunt;
  if (!away || (!away.sold.length && !wars.length && !halls.length && !hunt)) return;
  const ms = now - away.since;
  const rows: string[] = [];
  if (hunt) {
    const [l0, l1] = hunt.levels;
    rows.push(`<span class="wide">Your team kept hunting ${where(hunt.ground)} at ${awayPaceName()} for ${awayLength(hunt.ms)}${hunt.capped ? ' (today\'s limit)' : ''}.</span>`);
    rows.push(`<span>Coins</span><b>+${full(hunt.coins)}</b>`);
    rows.push(`<span>Trainer XP</span><b>+${full(hunt.xp)}${l1 > l0 ? ` <span class="muted">(Lv ${full(l0)} → ${full(l1)})</span>` : ''}</b>`);
    for (const [id, from, to] of hunt.team) {
      const c = S.creatures.get(id);
      if (c) rows.push(`<span>${esc(cname(c))}</span><b>Lv ${full(from)} → ${full(to)}</b>`);
    }
    rows.push('<span>Catches and drops only happen with the game open.</span>');
  }
  for (const h of halls) {
    const hall = isHall(h.element) ? hallName(h.element) : 'A hall';
    rows.push(`<span class="wide">Lost the ${esc(hall)}: ${h.why === 'challenge' && h.to ? `${esc(h.to)} beat your team` : h.to ? `${esc(h.to)}'s team holds it now` : 'now vacant'}</span>`);
  }
  for (const w of wars.slice(-3)) {
    rows.push(`<span>Guild War #${w.no}</span><b>${esc(`[${w.tag}]`)} ${ordinal(w.rank)} of ${w.of} · ${full(w.gp)} GP${w.bonus ? ` · +${w.bonus}% bonus` : w.qualified ? '' : ' <span class="muted">(no bonus)</span>'}</b>`);
  }
  if (away.sold.length) {
    const coins = away.sold.reduce((s, x) => s + x.price - x.fee, 0);
    rows.push(`<span>Sold on the market</span><b>${away.sold.length} · +${full(coins)} coins <span class="muted">after fees</span></b>`);
  }
  for (const x of away.sold.slice(-4)) {
    const name = x.kind === 'item' ? `${x.qty} × ${esc(ITEM[x.ref]?.name ?? x.ref)}` : x.kind === 'creature' ? (x.species && SPECIES[x.species] ? `${x.aura ? 'Aura ' : ''}${esc(SPECIES[x.species].name)}${x.level ? ` Lv ${x.level}` : ''}` : 'A creature') : x.kind === 'shrine' ? 'A shrine' : x.kind === 'held' ? 'A held item' : 'A relic';
    rows.push(`<span class="muted">${name}</span><b class="muted">${full(x.price)}</b>`);
  }
  const title = `Since your last visit (${awayLength(ms)} ago)`;
  // A tab that was hidden and then lost its connection: keep both parts.
  if (card) card.rows.push(...rows);
  else card = { title, rows, since: away.since, caught: false };
}

export function closeAway() { card = null; renderAway(); }
export const awaySince = () => card?.since ?? 0;

let following = false;
/** Wide screens: keep the card just under the hunt card, whose height changes with the hunt. */
function followHuntCard(host: HTMLElement) {
  const head = document.getElementById('fieldhead');
  if (!head) return;
  const place = () => host.style.setProperty('--away-top', `${head.offsetTop + head.offsetHeight + 8}px`);
  place();
  if (!following && typeof ResizeObserver !== 'undefined') { following = true; new ResizeObserver(place).observe(head); }
}

export function renderAway() {
  const host = document.getElementById('away');
  if (!host) return;
  host.hidden = !card;
  if (!card) return patch(host, '');
  followHuntCard(host);
  patch(host, `<div class="aw-h"><b>${esc(card.title)}</b><button class="iconbtn" data-act="away-close" aria-label="Close">×</button></div>
    <div class="aw-rows">${card.rows.map((r) => `<div>${r}</div>`).join('')}</div>
    ${card.caught ? '<div class="aw-f"><button class="btn small" data-act="away-catches">See new catches</button></div>' : ''}`);
}

// ---------------- hidden tab ----------------
document.addEventListener('visibilitychange', () => {
  if (document.hidden) startTally();
  else { finishTally(); renderAway(); }
});

// ---------------- travel ----------------
let region = '';
let travelTimer = 0;
/**
 * Called on render: a new region plays a 2 s "Traveling to …" over the field. Purely visual. While the hunt scene is
 * drawn, the walk to the new area (client/travel.ts) names the region instead.
 */
export function noteRegion() {
  const g = S.trainer ? GROUND[S.trainer.ground] : undefined;
  if (!g) return;
  const was = region;
  region = g.region;
  if (!was || was === g.region || document.hidden || fieldWatching()) return;
  const host = document.getElementById('travel');
  if (!host) return;
  host.textContent = `Traveling to ${REGIONS.find((r) => r.id === g.region)?.name ?? 'a new region'}…`;
  host.hidden = false;
  host.classList.remove('go'); void host.offsetWidth; host.classList.add('go');
  clearTimeout(travelTimer);
  travelTimer = window.setTimeout(() => { host.hidden = true; host.classList.remove('go'); }, TRAVEL_MS);
}
